import type { TFunction } from "i18next";
import { AlertCircleIcon, InfoIcon, Loader2, Save, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CurrentMonthBudgetField } from "./CurrentMonthBudgetField";
import { IntegrationModuleSelect } from "./IntegrationModuleSelect";
import { ProRataFirstMonthField } from "./ProRataFirstMonthField";
import type { CampaignJobBoardFormControl } from "./types";
import type { CampaignBudgetMinimums } from "../../api/useCampaignBudgetMinimums";
import { MAX_MONTHLY_BUDGET, MIN_MONTHLY_BUDGET } from "../../constants";
import { useCampaignJobBoardForm } from "../../hooks/useCampaignJobBoardForm";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import { DatePicker } from "@/components/ui/v2/date-picker";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import { AddCampaignJobBoardRequest } from "@/types/request/AddCampaignJobBoardRequest";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { dateToString, isBeforeToday, stringToDate } from "@/utils/dateUtils";

export type CampaignJobBoardFormProps = {
  campaignId: string;
  campaignJobBoard?: CampaignJobBoardType;
  clientId?: string;
  disabledIntegrationModules?: string[];
  errorMessage?: string | null;
  isEdit?: boolean;
  isSubmitting: boolean;
  loadingDisabledIntegrationModules?: boolean;
  onSubmitForm: (data: AddCampaignJobBoardRequest) => void;
  onSuccess?: () => void;
};

function CampaignJobBoardFormContent({
  campaignId,
  campaignJobBoard,
  clientId,
  disabledIntegrationModules,
  isEdit = false,
  isSubmitting,
  loadingDisabledIntegrationModules,
  onSubmitForm,
  onSuccess,
  errorMessage,
}: CampaignJobBoardFormProps) {
  const { t } = useTranslation();

  const {
    form,
    control,
    formValues,
    dateConstraints,
    useDifferentCurrentMonthBudget,
    showCurrentMonthBudget,
    daysRemainingInMonth,
    isIndeed,
    setIntegrationKey,
    handleCancel,
    handleSubmit,
    budgetMinimums,
  } = useCampaignJobBoardForm({
    campaignId,
    campaignJobBoard,
    clientId,
    isEdit,
    onSuccess,
  });

  const [integrationName, setIntegrationName] = useState<string | undefined>(
    campaignJobBoard?.integrationDisplayName ?? undefined
  );

  const onSubmit = form.handleSubmit(handleSubmit(onSubmitForm));

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-6">
        {!isEdit && (
          <FormField
            control={control}
            name="clientIntegrationModuleId"
            render={({ field: _field }) => (
              <FormItem>
                <IntegrationModuleSelect
                  fieldName="clientIntegrationModuleId"
                  label={t("common.jobBoard")}
                  placeholder={t("admin.campaigns.jobBoard.select", {
                    defaultValue: t("common.select"),
                  })}
                  disabled={loadingDisabledIntegrationModules}
                  disabledOptions={disabledIntegrationModules}
                  clientId={clientId}
                  useFormLabel={true}
                  moduleFilter="campaigns"
                  displayAsIntegrationName
                  onIntegrationNameChange={setIntegrationName}
                  onIntegrationKeyChange={setIntegrationKey}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <DateFields
            control={control}
            isEdit={isEdit}
            dateConstraints={dateConstraints}
            preventEndDateClear={isEdit && isIndeed && !!campaignJobBoard?.endDate}
            t={t}
          />
        </div>

        <BudgetFields control={control} t={t} isEdit={isEdit} budgetMinimums={budgetMinimums} integrationDisplayName={integrationName} />

        {isIndeed ? (
          <ProRataFirstMonthField
            control={control}
            useProRataFirstMonth={formValues.useProRataFirstMonth ?? false}
            showCurrentMonthBudget={showCurrentMonthBudget}
            daysRemainingInMonth={daysRemainingInMonth}
            t={t}
          />
        ) : (
          <CurrentMonthBudgetField
            control={control}
            useDifferentCurrentMonthBudget={useDifferentCurrentMonthBudget}
            showCurrentMonthBudget={showCurrentMonthBudget}
            daysRemainingInMonth={daysRemainingInMonth}
            monthlyBudget={formValues.monthlyBudget}
            t={t}
          />
        )}

        {errorMessage ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mt-6">
            {errorMessage}
          </div>
        ) : null}

        <div className="flex items-center justify-start gap-2 mt-6">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            {t("common.save")}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel}>
            <X className="size-4" />
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
type DateFieldsProps = {
  control: CampaignJobBoardFormControl;
  isEdit: boolean;
  dateConstraints: {
    minStartDate: Date;
    maxStartDate: Date;
    minEndDate: Date;
  };
  preventEndDateClear: boolean;
  t: TFunction;
};

function DateFields({ control, isEdit, dateConstraints, preventEndDateClear, t }: DateFieldsProps) {
  const { minStartDate, maxStartDate, minEndDate } = dateConstraints;

  return (
    <>
      <FormField
        control={control}
        name="startDate"
        render={({ field }) => (
          <FormItem>
            <span className="text-sm font-medium text-foreground">
              {t("common.startDate")}
            </span>
            <FormControl>
              <DatePicker
                value={stringToDate(field.value)}
                onChange={(date) => field.onChange(dateToString(date))}
                placeholder={t("common.startDate")}
                disabled={isEdit && isBeforeToday(field.value)}
                minDate={minStartDate}
                maxDate={maxStartDate}
              />
            </FormControl>
            <FormDescription>
              {t("admin.campaigns.jobBoard.startDateDescription", {
                defaultValue:
                  "The date when the job board publication will start.",
              })}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="endDate"
        render={({ field }) => (
          <FormItem>
            <span className="text-sm font-medium text-foreground">
              {t("common.endDate")}
            </span>
            <FormControl>
              <DatePicker
                value={stringToDate(field.value)}
                onChange={(date) => {
                  field.onChange(date ? dateToString(date) : "");
                }}
                placeholder={t("common.endDate")}
                disabled={
                  isEdit && field.value ? isBeforeToday(field.value) : false
                }
                minDate={minEndDate}
                allowClear={!preventEndDateClear}
              />
            </FormControl>
            <FormDescription>
              {preventEndDateClear
                ? t("admin.campaigns.jobBoard.indeedEndDateCannotBeCleared", {
                  defaultValue:
                      "Indeed does not allow removing an end date once set. You can change it to a different date.",
                })
                : t("admin.campaigns.jobBoard.endDateDescription", {
                  defaultValue:
                      "Optional end date for the publication. Leave empty for no end date.",
                })}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

type BudgetFieldsProps = {
  control: CampaignJobBoardFormControl;
  t: TFunction;
  isEdit: boolean;
  budgetMinimums?: CampaignBudgetMinimums;
  integrationDisplayName?: string;
};

function BudgetFields({ control, t, isEdit, budgetMinimums, integrationDisplayName }: BudgetFieldsProps) {
  // Calculate the effective month (2 months from now)
  const effectiveDate = new Date();
  effectiveDate.setMonth(effectiveDate.getMonth() + 2);
  const effectiveMonthName = effectiveDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-3">
      <FormField
        control={control}
        name="monthlyBudget"
        render={({ field }) => (
          <FormItem>
            <span className="text-sm font-medium text-foreground">{`${t(
              "common.monthly"
            )} ${t("common.budget")}`}</span>
            <FormControl>
              <FormattedCurrencyInput
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value ?? null)}
                maxValue={MAX_MONTHLY_BUDGET}
                placeholder="0"
              />
            </FormControl>
            <FormDescription>
              {t("admin.campaigns.jobBoard.monthlyBudgetDescription", {
                defaultValue:
                  "Default monthly budget for this job board. Minimum: {{min}}, Maximum: {{max}}",
                min: MIN_MONTHLY_BUDGET.toLocaleString(),
                max: MAX_MONTHLY_BUDGET.toLocaleString(),
              })}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      {!isEdit && budgetMinimums && (budgetMinimums.minimumMonthlyBudget != null || budgetMinimums.minimumBudgetPerVacancy != null) && (() => {
        const name = integrationDisplayName ?? t("common.jobBoard");
        const effectiveMin = Math.max(
          budgetMinimums.minimumMonthlyBudget ?? 0,
          (budgetMinimums.minimumBudgetPerVacancy ?? 0) * budgetMinimums.vacancyCount,
        );
        return (
          <Alert>
            <InfoIcon className="size-4" />
            <AlertDescription>
              {t("admin.campaigns.jobBoard.minimumBudgetRequirement", {
                defaultValue: "{{name}} requires a minimum budget of €{{minimum}} for the selected segment of {{count}} vacancies.",
                name,
                minimum: effectiveMin.toLocaleString(),
                count: budgetMinimums.vacancyCount,
              })}
            </AlertDescription>
          </Alert>
        );
      })()}
      {isEdit && (
        <Alert>
          <AlertCircleIcon />
          <AlertDescription>
            {t("admin.campaigns.jobBoard.budgetEffectiveDate", {
              defaultValue:
                "This budget will apply to new months starting from <strong>{{month}}</strong>. Budgets for current and next month have already been created and can be edited individually in their respective month views.",
              month: effectiveMonthName,
            })
              .split("<strong>")
              .map((part, i) => {
                if (i === 0) return part;
                const [bold, rest] = part.split("</strong>");
                return (
                  <span key={i}>
                    <strong className="font-semibold">{bold}</strong>
                    {rest}
                  </span>
                );
              })}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function CampaignJobBoardForm(props: CampaignJobBoardFormProps) {
  return (
    <div className="max-w-lg">
      <CampaignJobBoardFormContent {...props} />
    </div>
  );
}

export default CampaignJobBoardForm;
